import { FileText, Server, ShoppingBag, Lock, FolderOpen, ExternalLink } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDocumentation = () => {
  return (
    <div className="space-y-6" data-testid="documentation-page">
      <div>
        <h1 className="font-serif text-2xl text-[#2C2C2C] mb-1">Documentation</h1>
        <p className="text-stone-600 text-sm">Deployment, configuration, and administration reference</p>
      </div>

      {/* Deployment */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-serif flex items-center gap-2">
            <Server className="w-5 h-5 text-[#4A6763]" strokeWidth={1.5} />
            Deployment
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 text-sm text-stone-600">
          <div className="p-4 bg-stone-50 rounded-lg space-y-2">
            <p className="font-medium text-stone-800">Current Deployment</p>
            <div className="grid gap-1">
              <p><span className="text-stone-500">App URL:</span> <code className="text-xs bg-stone-100 px-1 rounded" data-testid="doc-app-url">{BACKEND_URL}</code></p>
              <p><span className="text-stone-500">Backend:</span> FastAPI (Python) on port 8001</p>
              <p><span className="text-stone-500">Frontend:</span> React (Node.js) on port 3000</p>
              <p><span className="text-stone-500">Database:</span> MongoDB (local)</p>
            </div>
          </div>
          <div>
            <p className="font-medium text-stone-800 mb-2">To deploy to production:</p>
            <ol className="list-decimal ml-5 space-y-1.5">
              <li>Use the <strong>"Save to Github"</strong> button to push the code to your repository</li>
              <li>Deploy the backend (FastAPI) to any Python hosting (Railway, Render, AWS, etc.)</li>
              <li>Deploy the frontend (React) to any static hosting (Vercel, Netlify, etc.)</li>
              <li>Set environment variables on both services (see Configuration section below)</li>
              <li>Update the <code className="bg-stone-100 px-1 rounded text-xs">REACT_APP_BACKEND_URL</code> in the frontend to point to your production backend</li>
            </ol>
          </div>
        </CardContent>
      </Card>

      {/* Shopify Connection */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-serif flex items-center gap-2">
            <ShoppingBag className="w-5 h-5 text-[#C7A07A]" strokeWidth={1.5} />
            Shopify Connection
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 text-sm text-stone-600">
          <div className="p-4 bg-stone-50 rounded-lg space-y-2">
            <p className="font-medium text-stone-800">Current Connection</p>
            <div className="grid gap-1">
              <p><span className="text-stone-500">Store:</span> <code className="text-xs bg-stone-100 px-1 rounded">fasttracktohealth.shop</code></p>
              <p><span className="text-stone-500">API:</span> Shopify Storefront API (GraphQL)</p>
              <p><span className="text-stone-500">Channel:</span> Headless</p>
              <p><span className="text-stone-500">Products:</span> 408 synced</p>
            </div>
          </div>
          <div>
            <p className="font-medium text-stone-800 mb-2">How Shopify is connected:</p>
            <ol className="list-decimal ml-5 space-y-1.5">
              <li>A <strong>Headless</strong> sales channel is configured in your Shopify admin</li>
              <li>The <strong>Storefront API access token</strong> is stored in the backend <code className="bg-stone-100 px-1 rounded text-xs">.env</code> file</li>
              <li>Products are imported via <strong>Admin &rarr; Products &rarr; Sync from Shopify</strong></li>
              <li>Cart checkout creates a Shopify cart and redirects customers to <code className="bg-stone-100 px-1 rounded text-xs">fasttracktohealth.shop/cart</code></li>
            </ol>
          </div>
          <div>
            <p className="font-medium text-stone-800 mb-2">To change the Shopify store:</p>
            <ol className="list-decimal ml-5 space-y-1.5">
              <li>Go to <strong>Admin &rarr; Settings</strong></li>
              <li>Update <strong>Shopify Store Domain</strong> and <strong>Storefront Access Token</strong></li>
              <li>Click Save, then go to <strong>Products &rarr; Sync from Shopify</strong></li>
            </ol>
          </div>
        </CardContent>
      </Card>

      {/* Admin Login */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-serif flex items-center gap-2">
            <Lock className="w-5 h-5 text-red-500" strokeWidth={1.5} />
            Admin Access
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 text-sm text-stone-600">
          <div className="p-4 bg-stone-50 rounded-lg space-y-2">
            <p className="font-medium text-stone-800">Login Details</p>
            <div className="grid gap-1">
              <p><span className="text-stone-500">Admin URL:</span> <code className="text-xs bg-stone-100 px-1 rounded">{BACKEND_URL}/admin</code></p>
              <p><span className="text-stone-500">Auth Method:</span> Password-based (HTTP Basic Auth)</p>
              <p><span className="text-stone-500">Password location:</span> <code className="text-xs bg-stone-100 px-1 rounded">backend/.env &rarr; ADMIN_PASSWORD</code></p>
            </div>
          </div>
          <div>
            <p className="font-medium text-stone-800 mb-2">To change the admin password:</p>
            <ol className="list-decimal ml-5 space-y-1.5">
              <li>Edit <code className="bg-stone-100 px-1 rounded text-xs">backend/.env</code></li>
              <li>Change the <code className="bg-stone-100 px-1 rounded text-xs">ADMIN_PASSWORD</code> value</li>
              <li>Restart the backend service</li>
            </ol>
          </div>
        </CardContent>
      </Card>

      {/* Configuration Files */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-serif flex items-center gap-2">
            <FolderOpen className="w-5 h-5 text-blue-600" strokeWidth={1.5} />
            Configuration Files
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3 text-sm">
            <div className="p-3 bg-stone-50 rounded-lg">
              <p className="font-mono text-xs text-stone-800 font-medium">backend/.env</p>
              <ul className="mt-2 space-y-1 text-xs text-stone-600">
                <li><code className="bg-stone-100 px-1 rounded">MONGO_URL</code> — MongoDB connection string</li>
                <li><code className="bg-stone-100 px-1 rounded">DB_NAME</code> — Database name</li>
                <li><code className="bg-stone-100 px-1 rounded">ADMIN_PASSWORD</code> — Admin dashboard password</li>
                <li><code className="bg-stone-100 px-1 rounded">SHOPIFY_STORE_DOMAIN</code> — Your Shopify store domain</li>
                <li><code className="bg-stone-100 px-1 rounded">SHOPIFY_STOREFRONT_TOKEN</code> — Storefront API access token</li>
                <li><code className="bg-stone-100 px-1 rounded">CORS_ORIGINS</code> — Allowed frontend origins</li>
              </ul>
            </div>
            <div className="p-3 bg-stone-50 rounded-lg">
              <p className="font-mono text-xs text-stone-800 font-medium">frontend/.env</p>
              <ul className="mt-2 space-y-1 text-xs text-stone-600">
                <li><code className="bg-stone-100 px-1 rounded">REACT_APP_BACKEND_URL</code> — Backend API URL (used for all API calls)</li>
              </ul>
            </div>
            <div className="p-3 bg-stone-50 rounded-lg">
              <p className="font-mono text-xs text-stone-800 font-medium">backend/server.py</p>
              <p className="mt-1 text-xs text-stone-600">Main backend — all API endpoints, Shopify integration, scoring engine, admin routes</p>
            </div>
            <div className="p-3 bg-stone-50 rounded-lg">
              <p className="font-mono text-xs text-stone-800 font-medium">frontend/src/lib/api.js</p>
              <p className="mt-1 text-xs text-stone-600">All frontend API call functions — public quiz, Shopify, admin, analytics</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Quick Links */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-serif">Quick Links</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-3">
            {[
              { label: 'Landing Page', url: '/', desc: 'Customer-facing homepage' },
              { label: 'Quiz', url: '/quiz', desc: 'Direct quiz link' },
              { label: 'Embed Quiz', url: '/embed', desc: 'Embeddable version for Shopify' },
              { label: 'Admin Dashboard', url: '/admin', desc: 'Admin panel' },
              { label: 'Analytics', url: '/admin/analytics', desc: 'Quiz analytics' },
              { label: 'Embed Guide', url: '/admin/embed-guide', desc: 'Shopify embedding instructions' },
              { label: 'Import/Export', url: '/admin/import', desc: 'Backup and restore data' },
              { label: 'Settings', url: '/admin/settings', desc: 'App configuration' },
            ].map(link => (
              <a
                key={link.url}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-between p-3 bg-stone-50 rounded-lg hover:bg-stone-100 transition-colors group"
                data-testid={`doc-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div>
                  <p className="text-sm font-medium text-stone-800 group-hover:text-[#4A6763]">{link.label}</p>
                  <p className="text-xs text-stone-500">{link.desc}</p>
                </div>
                <ExternalLink className="w-4 h-4 text-stone-400 group-hover:text-[#4A6763]" strokeWidth={1.5} />
              </a>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminDocumentation;
